import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth, can } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import PropertyCard from "@/components/PropertyCard";
import PropertyDialog from "@/components/PropertyDialog";
import { Button } from "@/components/ui/button";
import { Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PROPERTIES } from "@/constants/testIds";

export default function Properties() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [byProp, setByProp] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    const [pr, dp] = await Promise.all([
      api.get("/properties"),
      api.get("/dashboard/by_property"),
    ]);
    setItems(pr.data);
    const map = {};
    for (const x of dp.data) map[x.property_id] = x;
    setByProp(map);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      (p.name + " " + p.city + " " + p.address + " " + p.country).toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">{t("nav.properties")}</h1>
          <div className="text-sm text-muted-foreground mt-1">
            {items.length} propiedades — {Object.keys(byProp).length ? "rendimiento calculado en USD" : "cargando…"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar propiedad, ciudad…"
              className="pl-9 w-72"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="properties-search"
            />
          </div>
          {can(user, "manageProperties") && (
            <Button
              className="rounded-full"
              onClick={() => setDialogOpen(true)}
              data-testid={PROPERTIES.addBtn}
            >
              <Plus className="w-4 h-4 mr-1" /> {t("prop.add")}
            </Button>
          )}
        </div>
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
        data-testid={PROPERTIES.grid}
      >
        {filtered.map((p) => {
          const stats = byProp[p.id] || { roi: 0, net: 0, value_usd: 0 };
          return (
            <PropertyCard
              key={p.id}
              property={p}
              roi={stats.roi}
              netUsd={stats.net}
              valueUsd={stats.value_usd}
            />
          );
        })}
        {!filtered.length && (
          <div className="col-span-full text-center py-16 text-muted-foreground">
            Sin propiedades para mostrar.
          </div>
        )}
      </div>

      <PropertyDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        property={null}
        onSaved={load}
      />
    </div>
  );
}
